
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Tooltip,
    alpha,
    useTheme,
    Tabs,
    Tab,
    Chip,
    Avatar,
    InputAdornment,
    TablePagination,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Skeleton
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    ReportProblem as ReportProblemIcon,
    History as HistoryIcon,
    Warning as WarningIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Image as ImageIcon,
    Search as SearchIcon,
    MoreVert as MoreVertIcon,
    Visibility as ViewIcon,
    Close as CloseIcon,
    Phone as PhoneIcon,
    LocationOn as LocationIcon,
    Category as CategoryIcon,
    Assignment as AssignmentIcon,
    AccountCircle as AccountIcon,
    CalendarMonth as CalendarIcon,
    Description as DescriptionIcon,
    Comment as CommentIcon,
    Photo as PhotoIcon,
    Map as MapIcon
} from '@mui/icons-material';
import { grievanceService } from '../service/grievanceService';
import dayjs from 'dayjs';
import { getImageUrl } from '../service/api';
import SuccessNotification from '../components/SuccessNotification';

export default function Grievances() {
    const theme = useTheme();
    const [tabValue, setTabValue] = useState(0);
    const [grievanceTypes, setGrievanceTypes] = useState([]);
    const [userGrievances, setUserGrievances] = useState([]);
    const [loading, setLoading] = useState(true);

    // Modal state for Type management
    const [open, setOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [grievanceName, setGrievanceName] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    // Menu state
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedData, setSelectedData] = useState(null);
    const [viewModalOpen, setViewModalOpen] = useState(false);

    // Pagination state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);

    // Status Update State
    const [statusUpdateOpen, setStatusUpdateOpen] = useState(false);
    const [statusPayload, setStatusPayload] = useState({ id: null, status: '', remarks: '' });
    const [statusLoading, setStatusLoading] = useState(false);

    // Notification State
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        setLoading(true);
        const timer = setTimeout(() => {
            if (tabValue === 0) {
                fetchGrievanceTypes();
            } else {
                fetchUserGrievances();
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [tabValue, page, rowsPerPage, searchTerm]);

    const fetchGrievanceTypes = async () => {
        setLoading(true);
        try {
            // Ensure loading lasts for at least 1 second
            const [response] = await Promise.all([
                grievanceService.getGrievances(page + 1, rowsPerPage, searchTerm),
                new Promise(resolve => setTimeout(resolve, 1000))
            ]);
            if (response.success) {
                if (response.data && response.data.data) {
                    setGrievanceTypes(response.data.data);
                    setTotalCount(response.data.totalRecords || 0);
                } else {
                    const data = response.data || [];
                    setGrievanceTypes(data);
                    setTotalCount(data.length);
                }
            }
        } catch (error) {
            console.error("Error fetching grievance types", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchUserGrievances = async () => {
        setLoading(true);
        try {
            // Ensure loading lasts for at least 1 second
            const [response] = await Promise.all([
                grievanceService.getAllGrievances(page + 1, rowsPerPage, searchTerm),
                new Promise(resolve => setTimeout(resolve, 1000))
            ]);
            if (response.success) {
                if (response.data && response.data.data) {
                    setUserGrievances(response.data.data);
                    setTotalCount(response.data.totalRecords || 0);
                } else {
                    const data = response.data || [];
                    setUserGrievances(data);
                    setTotalCount(data.length);
                }
            }
        } catch (error) {
            console.error("Error fetching user grievances", error);
        } finally {
            setLoading(false);
        }
    };

    const handleStatusChange = (item, newStatus) => {
        setStatusPayload({ 
            ...item, 
            status: newStatus, 
            remarks: item.remarks || '' 
        });
        setStatusUpdateOpen(true);
    };

    const handleConfirmStatusUpdate = async () => {
        if (!statusPayload.id) return;
        
        setStatusLoading(true);
        try {
            const response = await grievanceService.updateUserGrievanceStatus(
                statusPayload.id, 
                parseInt(statusPayload.status),
                statusPayload.remarks
            );
            
            if (response.success) {
                fetchUserGrievances();
                setStatusUpdateOpen(false);
                setNotification({ 
                    open: true, 
                    message: 'Grievance status updated successfully!', 
                    severity: 'success' 
                });
            } else {
                setNotification({ 
                    open: true, 
                    message: response.message || 'Failed to update status', 
                    severity: 'error' 
                });
            }
        } catch (error) {
            console.error("Error updating status", error);
            setNotification({ 
                open: true, 
                message: 'An error occurred while updating status', 
                severity: 'error' 
            });
        } finally {
            setStatusLoading(false);
        }
    };

    const handleMenuOpen = (event, data) => {
        setAnchorEl(event.currentTarget);
        setSelectedData(data);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedData(null);
    };

    const handleViewOpen = (data) => {
        setSelectedData(data);
        setViewModalOpen(true);
    };

    const handleOpen = () => {
        setIsEditing(false);
        setGrievanceName('');
        setOpen(true);
    };

    const handleEditOpen = (type) => {
        setIsEditing(true);
        setSelectedId(type.id);
        setGrievanceName(type.grievanceName || '');
        setOpen(true);
    };

    const handleSubmit = async () => {
        if (!grievanceName.trim()) return;
        setLoading(true);
        try {
            let response;
            if (isEditing) {
                response = await grievanceService.updateGrievance(selectedId, grievanceName);
            } else {
                response = await grievanceService.addGrievance(grievanceName);
            }
            if (response.success) {
                fetchGrievanceTypes();
                setOpen(false);
                setNotification({ open: true, message: `Category ${isEditing ? 'updated' : 'created'} successfully!`, severity: 'success' });
            }
        } catch (error) {
            console.error("Error saving grievance type", error);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClick = (id) => {
        setSelectedId(id);
        setDeleteModalOpen(true);
    };

    const handleDeleteConfirm = async () => {
        setLoading(true);
        try {
            const response = await grievanceService.deleteGrievance(selectedId);
            if (response.success) {
                fetchGrievanceTypes();
                setDeleteModalOpen(false);
                setNotification({ open: true, message: 'Category deleted successfully!', severity: 'success' });
            }
        } catch (error) {
            console.error("Error deleting grievance type", error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ pb: 5 }}>
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                mb: { xs: 3, sm: 4 },
                p: { xs: 2, sm: 3 },
                borderRadius: 4,
                bgcolor: 'white',
                boxShadow: '0 2px 12px rgba(0,0,0,0.03)',
                gap: 2
            }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: '#1e293b', letterSpacing: '-0.5px', fontSize: { xs: '1.5rem', sm: '2rem' } }}>Grievances</Typography>
                </Box>
                {tabValue === 0 && (
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleOpen}
                        sx={{
                            borderRadius: 2.5,
                            px: 3,
                            py: 1.2,
                            width: { xs: '100%', sm: 'auto' },
                            textTransform: 'none',
                            fontWeight: 700,
                            fontSize: '0.95rem',
                            background: 'primary.main',
                            boxShadow: `0 4px 14px 0 ${alpha(theme.palette.secondary.main, 0.39)}`,
                            '&:hover': {
                                background: 'primary.dark',
                                boxShadow: `0 6px 20px ${alpha(theme.palette.secondary.main, 0.23)}`
                            }
                        }}
                    >
                        Add Category
                    </Button>
                )}
            </Box>

            <Box sx={{
                bgcolor: 'white',
                borderRadius: 4,
                p: 0.5,
                mb: 3,
                display: 'flex',
                width: 'fit-content',
                maxWidth: '100%',
                overflowX: 'auto',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                border: '1px solid #f1f5f9'
            }}>
                <Tabs
                    value={tabValue}
                    onChange={(e, v) => setTabValue(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        minHeight: 44,
                        '& .MuiTabs-indicator': { display: 'none' },
                        '& .MuiTab-root': {
                            minHeight: 40,
                            borderRadius: 3,
                            px: { xs: 2, sm: 3 },
                            fontWeight: 700,
                            textTransform: 'none',
                            fontSize: '0.9rem',
                            color: '#64748b',
                            '&.Mui-selected': {
                                color: 'white',
                                bgcolor: 'primary.main',
                                boxShadow: `0 4px 12px ${alpha(theme.palette.secondary.main, 0.25)}`
                            },
                            transition: 'all 0.2s'
                        }
                    }}
                >
                    <Tab label="Categories" icon={<ReportProblemIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
                    <Tab label="Public Petitions" icon={<HistoryIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
                </Tabs>
            </Box>

            <Paper sx={{
                borderRadius: 4,
                overflow: 'hidden',
                boxShadow: '0 10px 30px 0 rgba(0,0,0,0.04)',
                border: '1px solid rgba(226, 232, 240, 0.8)',
                mb: 4
            }}>
                <Box sx={{ p: { xs: 2, sm: 3 }, borderBottom: '1px solid', borderColor: '#f1f5f9', bgcolor: '#ffffff' }}>
                    <TextField
                        fullWidth
                        placeholder={tabValue === 0 ? "Search categories..." : "Search petitions by name or description..."}
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setPage(0);
                        }}
                        variant="outlined"
                        size="small"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" sx={{ color: '#94a3b8' }} />
                                </InputAdornment>
                            ),
                            sx: {
                                bgcolor: '#f8fafc',
                                borderRadius: 3,
                                '& fieldset': { border: 'none' },
                                '&:hover': { bgcolor: '#f1f5f9' },
                                transition: 'all 0.2s'
                            }
                        }}
                    />
                </Box>

                {
                    tabValue === 0 ? (
                        <TableContainer sx={{ overflowX: 'auto' }}>
                            <Table sx={{ minWidth: 600 }}>
                                <TableHead sx={{ bgcolor: '#f8fafc' }}>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.85rem', textTransform: 'uppercase', py: 2 }}>Actions</TableCell>
                                        <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.85rem', textTransform: 'uppercase' }}>ID</TableCell>
                                        <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.85rem', textTransform: 'uppercase' }}>Category Name</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {loading ? (
                                        Array.from(new Array(rowsPerPage)).map((_, index) => (
                                            <TableRow key={`skeleton-cat-${index}`}>
                                                <TableCell><Skeleton variant="circular" width={32} height={32} /></TableCell>
                                                <TableCell><Skeleton width="40px" /></TableCell>
                                                <TableCell><Skeleton width="200px" /></TableCell>
                                            </TableRow>
                                        ))
                                    ) : grievanceTypes.length > 0 ? (
                                        (grievanceTypes.length > rowsPerPage ? grievanceTypes.slice(page * rowsPerPage, (page + 1) * rowsPerPage) : grievanceTypes).map((type) => (
                                            <TableRow key={type.id} hover sx={{ '&:hover': { bgcolor: 'rgba(241, 245, 249, 0.5) !important' }, transition: 'all 0.2s' }}>
                                                <TableCell>
                                                    <IconButton onClick={(e) => handleMenuOpen(e, type)} sx={{ color: '#64748b' }}>
                                                        <MoreVertIcon fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                                <TableCell sx={{ color: '#94a3b8', fontWeight: 600 }}>#{type.id}</TableCell>
                                                <TableCell sx={{ fontWeight: 700, color: '#1e293b' }}>{type.grievanceName}</TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow><TableCell colSpan={3} align="center" sx={{ py: 5 }}>No categories found.</TableCell></TableRow>
                                    )}
                                </TableBody>
                            </Table>
                            <TablePagination
                                rowsPerPageOptions={[5, 10, 25]}
                                component="div"
                                count={totalCount}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={(e, p) => setPage(p)}
                                onRowsPerPageChange={(e) => {
                                    setRowsPerPage(parseInt(e.target.value, 10));
                                    setPage(0);
                                }}
                            />
                        </TableContainer>
                    ) : (
                        <TableContainer sx={{ overflowX: 'auto' }}>
                            <Table sx={{ minWidth: 1000 }}>
                                <TableHead sx={{ bgcolor: '#f8fafc' }}>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.85rem', textTransform: 'uppercase', py: 2 }}>Actions</TableCell>
                                        <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.85rem', textTransform: 'uppercase' }}>ID & Date</TableCell>
                                        <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.85rem', textTransform: 'uppercase' }}>User Phone</TableCell>
                                        <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.85rem', textTransform: 'uppercase' }}>Type</TableCell>
                                        <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.85rem', textTransform: 'uppercase' }}>Constituency</TableCell>
                                        <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.85rem', textTransform: 'uppercase' }}>Address</TableCell>
                                        <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.85rem', textTransform: 'uppercase' }}>Status</TableCell>
                                        <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.85rem', textTransform: 'uppercase' }}>Description</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {loading ? (
                                        Array.from(new Array(rowsPerPage)).map((_, index) => (
                                            <TableRow key={`skeleton-pet-${index}`}>
                                                <TableCell><Skeleton variant="circular" width={32} height={32} /></TableCell>
                                                <TableCell>
                                                    <Skeleton width="60px" sx={{ mb: 0.5 }} />
                                                    <Skeleton width="120px" height={12} />
                                                </TableCell>
                                                <TableCell><Skeleton width="90px" /></TableCell>
                                                <TableCell><Skeleton width="100px" height={24} sx={{ borderRadius: 1.5 }} /></TableCell>
                                                <TableCell><Skeleton width="120px" /></TableCell>
                                                <TableCell><Skeleton width="180px" /></TableCell>
                                                <TableCell><Skeleton width="120px" /></TableCell>
                                                <TableCell><Skeleton width="240px" /></TableCell>
                                            </TableRow>
                                        ))
                                    ) : userGrievances.length > 0 ? (
                                        (userGrievances.length > rowsPerPage ? userGrievances.slice(page * rowsPerPage, (page + 1) * rowsPerPage) : userGrievances).map((item) => (
                                            <TableRow key={item.id} hover sx={{ '&:hover': { bgcolor: 'rgba(241, 245, 249, 0.5) !important' }, transition: 'all 0.2s' }}>
                                                <TableCell>
                                                    <IconButton onClick={(e) => handleMenuOpen(e, item)} sx={{ color: '#64748b' }}>
                                                        <MoreVertIcon fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b' }}>
                                                        #{item.id}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 500 }}>
                                                        {dayjs(item.createdAt).format('DD MMM YYYY • HH:mm')}
                                                    </Typography>
                                                </TableCell>

                                                <TableCell>
                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#475569' }}>
                                                        {item.phone || '-'}
                                                    </Typography>
                                                </TableCell>

                                                <TableCell>
                                                    <Chip
                                                        label={item.grievanceTypeName || 'Other'}
                                                        size="small"
                                                        color="primary"
                                                        variant="outlined"
                                                        sx={{
                                                            fontWeight: 600,
                                                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                                                            color: theme.palette.primary.main,
                                                            borderColor: alpha(theme.palette.primary.main, 0.3),
                                                            borderRadius: 1.5
                                                        }}
                                                    />
                                                </TableCell>

                                                <TableCell sx={{ color: '#475569' }}>{item.constituencyName || '-'}</TableCell>

                                                <TableCell sx={{ maxWidth: 220, color: '#475569' }}>
                                                    <Typography variant="body2" noWrap title={item.address || ''}>
                                                        {item.address || '-'}
                                                    </Typography>
                                                </TableCell>

                                                <TableCell>
                                                    <TextField
                                                        select
                                                        size="small"
                                                        value={
                                                            (item.status || '').toLowerCase().replace(/\s/g, '') === 'inprogress' ? '2' :
                                                            ((item.status || '').toLowerCase().replace(/\s/g, '') === 'close' || (item.status || '').toLowerCase().replace(/\s/g, '') === 'closed') ? '3' : '1'
                                                        }
                                                        onChange={(e) => handleStatusChange(item, e.target.value)}
                                                        sx={{
                                                            minWidth: 120,
                                                            '& .MuiInputBase-root': {
                                                                height: 28,
                                                                fontSize: '0.7rem',
                                                                fontWeight: 700,
                                                                borderRadius: 1.5,
                                                                bgcolor: (((item.status || '').toLowerCase().replace(/\s/g, '') === 'close' || (item.status || '').toLowerCase().replace(/\s/g, '') === 'closed') ? alpha(theme.palette.success.main, 0.1) : 
                                                                         (item.status || '').toLowerCase().replace(/\s/g, '') === 'inprogress' ? alpha(theme.palette.warning.main, 0.1) : 
                                                                         alpha(theme.palette.error.main, 0.1)),
                                                                color: (((item.status || '').toLowerCase().replace(/\s/g, '') === 'close' || (item.status || '').toLowerCase().replace(/\s/g, '') === 'closed') ? theme.palette.success.dark : 
                                                                        (item.status || '').toLowerCase().replace(/\s/g, '') === 'inprogress' ? theme.palette.warning.dark : 
                                                                        theme.palette.error.dark),
                                                                '& fieldset': { border: 'none' }
                                                            }
                                                        }}
                                                        SelectProps={{
                                                            sx: { py: 0 }
                                                        }}
                                                    >
                                                        <MenuItem value="1" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Open</MenuItem>
                                                        <MenuItem value="2" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>In Progress</MenuItem>
                                                        <MenuItem value="3" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Close</MenuItem>
                                                    </TextField>
                                                </TableCell>

                                                <TableCell sx={{ maxWidth: 340, color: '#475569' }}>
                                                    <Typography variant="body2" sx={{ color: 'text.secondary' }} title={item.description || ''}>
                                                        {item.description || '-'}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow><TableCell colSpan={8} align="center" sx={{ py: 5 }}>No petitions found.</TableCell></TableRow>
                                    )}
                                </TableBody>
                            </Table>

                            <TablePagination
                                rowsPerPageOptions={[5, 10, 25]}
                                component="div"
                                count={totalCount}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={(e, p) => setPage(p)}
                                onRowsPerPageChange={(e) => {
                                    setRowsPerPage(parseInt(e.target.value, 10));
                                    setPage(0);
                                }}
                            />
                        </TableContainer>
                    )
                }
            </Paper>

            {/* Type Modal */}
            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ fontWeight: 700 }}>{isEditing ? 'Edit Category' : 'New Category'}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Grievance Category Name"
                        fullWidth
                        variant="outlined"
                        value={grievanceName}
                        onChange={(e) => setGrievanceName(e.target.value)}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 1 }}>
                    <Button onClick={() => setOpen(false)} color="inherit">Cancel</Button>
                    <Button onClick={handleSubmit} variant="contained" disabled={!grievanceName.trim()}>Save</Button>
                </DialogActions>
            </Dialog>

            {/* Delete Modal */}
            <Dialog open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogContent sx={{ textAlign: 'center', pt: 4 }}>
                    <WarningIcon sx={{ fontSize: 48, color: 'error.main', mb: 2 }} />
                    <Typography variant="h6" fontWeight={700}>Delete Category?</Typography>
                    <Typography variant="body2" color="text.secondary">This will permanently remove this category.</Typography>
                </DialogContent>
                <DialogActions sx={{ p: 3, justifyContent: 'center', gap: 2 }}>
                    <Button variant="outlined" onClick={() => setDeleteModalOpen(false)} sx={{ flex: 1 }}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleDeleteConfirm} sx={{ flex: 1 }}>Delete</Button>
                </DialogActions>
            </Dialog>

            {/* Status Update Modal */}
            <Dialog 
                open={statusUpdateOpen} 
                onClose={() => !statusLoading && setStatusUpdateOpen(false)} 
                maxWidth="xs" 
                fullWidth 
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Update Status</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Updating status for petition <b>#{statusPayload.id}</b>
                        </Typography>
                        
                        <TextField
                            select
                            label="New Status"
                            fullWidth
                            value={statusPayload.status}
                            onChange={(e) => setStatusPayload({ ...statusPayload, status: e.target.value })}
                            variant="outlined"
                            size="small"
                        >
                            <MenuItem value="1">Open</MenuItem>
                            <MenuItem value="2">In Progress</MenuItem>
                            <MenuItem value="3">Closed</MenuItem>
                        </TextField>

                        <TextField
                            label="Remarks / Comments"
                            fullWidth
                            multiline
                            rows={4}
                            placeholder="Enter any remarks about this status change..."
                            value={statusPayload.remarks}
                            onChange={(e) => setStatusPayload({ ...statusPayload, remarks: e.target.value })}
                            variant="outlined"
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 1 }}>
                    <Button 
                        onClick={() => setStatusUpdateOpen(false)} 
                        color="inherit" 
                        disabled={statusLoading}
                        sx={{ borderRadius: 2, px: 3 }}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleConfirmStatusUpdate} 
                        variant="contained" 
                        disabled={statusLoading}
                        sx={{ borderRadius: 2, px: 3, background: 'primary.main' }}
                        startIcon={statusLoading && <CircularProgress size={20} color="inherit" />}
                    >
                        {statusLoading ? 'Updating' : 'Update Status'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Actions Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{
                    sx: { minWidth: 160, borderRadius: 2.5, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }
                }}
            >
                <MenuItem onClick={() => { handleMenuClose(); handleViewOpen(selectedData); }}>
                    <ListItemIcon><ViewIcon fontSize="small" color="info" /></ListItemIcon>
                    <ListItemText primary="View" />
                </MenuItem>

                {tabValue === 0 && (
                    <MenuItem onClick={() => { handleMenuClose(); handleEditOpen(selectedData); }}>
                        <ListItemIcon><EditIcon fontSize="small" color="primary" /></ListItemIcon>
                        <ListItemText primary="Edit" />
                    </MenuItem>
                )}

                {tabValue === 0 && (
                    <MenuItem onClick={() => { handleMenuClose(); handleDeleteClick(selectedData.id); }} sx={{ color: 'error.main' }}>
                        <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
                        <ListItemText primary="Delete" />
                    </MenuItem>
                )}

                {tabValue === 1 && selectedData?.proofPicPath && (
                    <MenuItem onClick={() => { handleMenuClose(); window.open(getImageUrl(selectedData.proofPicPath), '_blank'); }}>
                        <ListItemIcon><ImageIcon fontSize="small" color="primary" /></ListItemIcon>
                        <ListItemText primary="View Proof" />
                    </MenuItem>
                )}
            </Menu>

            {/* View Modal */}
             <Dialog
                open={viewModalOpen}
                onClose={() => setViewModalOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{ 
                    sx: { 
                        borderRadius: 4,
                        boxShadow: '0 24px 48px -12px rgba(0,0,0,0.18)',
                        overflow: 'hidden'
                    } 
                }}
            >
                <Box sx={{ 
                    p: 3, 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    background: 'linear-gradient(90deg, #f8fafc 0%, #ffffff 100%)',
                    borderBottom: '1px solid #e2e8f0'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ 
                            p: 1.2, 
                            borderRadius: 2.5, 
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                            display: 'flex'
                        }}>
                            <AssignmentIcon />
                        </Box>
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b', lineHeight: 1.2 }}>
                                {tabValue === 0 ? 'Category Details' : 'Petition Details'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                                {selectedData && `#${selectedData.id}`} • {selectedData && dayjs(selectedData.createdAt).format('DD MMM YYYY')}
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton onClick={() => setViewModalOpen(false)} size="small" sx={{ color: '#94a3b8', '&:hover': { color: '#64748b' } }}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                <DialogContent sx={{ p: 4, bgcolor: '#ffffff' }}>
                    {selectedData && (
                        tabValue === 0 ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, py: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Box sx={{ p: 2, borderRadius: 3, bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main' }}>
                                        <CategoryIcon sx={{ fontSize: 32 }} />
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px' }}>Category Identification</Typography>
                                        <Typography variant="h5" sx={{ fontWeight: 800, color: '#1e293b' }}>{selectedData.grievanceName}</Typography>
                                        <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 600 }}>ID: #{selectedData.id}</Typography>
                                    </Box>
                                </Box>
                                
                                <Box sx={{ p: 3, borderRadius: 4, border: '1px solid #f1f5f9', bgcolor: '#f8fafc' }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#475569', mb: 1 }}>Usage Statistics</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        This category is used to group similar public grievances for easier management and reporting.
                                    </Typography>
                                </Box>
                            </Box>
                        ) : (
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 4 }}>
                                {/* Left Column: Core Details */}
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                    <Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                            <AccountIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#334155' }}>Basic Information</Typography>
                                        </Box>
                                        <Box sx={{ pl: 3.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                            <Box>
                                                <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Phone Number</Typography>
                                                <Typography variant="body1" sx={{ fontWeight: 600, color: '#1e293b' }}>{selectedData.phone || 'N/A'}</Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Category</Typography>
                                                <Box sx={{ mt: 0.5 }}>
                                                    <Chip 
                                                        icon={<CategoryIcon sx={{ fontSize: '14px !important' }} />}
                                                        label={selectedData.grievanceTypeName || 'General'} 
                                                        size="small"
                                                        sx={{ 
                                                            fontWeight: 700, 
                                                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                                                            color: theme.palette.primary.main,
                                                            border: '1px solid',
                                                            borderColor: alpha(theme.palette.primary.main, 0.2)
                                                        }} 
                                                    />
                                                </Box>
                                            </Box>
                                            <Box>
                                                <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Current Status</Typography>
                                                <Box sx={{ mt: 0.5 }}>
                                                    <Chip 
                                                        label={selectedData.status || 'Open'} 
                                                        size="small"
                                                        sx={{ 
                                                            fontWeight: 800,
                                                            px: 1,
                                                            textTransform: 'uppercase',
                                                            fontSize: '0.65rem',
                                                            bgcolor: (((selectedData.status || '').toLowerCase().replace(/\s/g, '') === 'close' || (selectedData.status || '').toLowerCase().replace(/\s/g, '') === 'closed') ? alpha(theme.palette.success.main, 0.1) : 
                                                                     (selectedData.status || '').toLowerCase().replace(/\s/g, '') === 'inprogress' ? alpha(theme.palette.warning.main, 0.1) : 
                                                                     alpha(theme.palette.error.main, 0.1)),
                                                            color: (((selectedData.status || '').toLowerCase().replace(/\s/g, '') === 'close' || (selectedData.status || '').toLowerCase().replace(/\s/g, '') === 'closed') ? theme.palette.success.dark : 
                                                                    (selectedData.status || '').toLowerCase().replace(/\s/g, '') === 'inprogress' ? theme.palette.warning.dark : 
                                                                    theme.palette.error.dark),
                                                        }} 
                                                    />
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Box>

                                    <Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                            <MapIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#334155' }}>Location Details</Typography>
                                        </Box>
                                        <Box sx={{ pl: 3.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                            <Box>
                                                <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Constituency</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#475569' }}>{selectedData.constituencyName || '-'}</Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Address</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 500, color: '#475569', lineHeight: 1.6 }}>{selectedData.address || '-'}</Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>

                                {/* Right Column: Content & Response */}
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                    <Box sx={{ p: 2.5, borderRadius: 3, bgcolor: '#f8fafc', border: '1px dashed #e2e8f0' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                            <DescriptionIcon sx={{ fontSize: 18, color: '#64748b' }} />
                                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 800, textTransform: 'uppercase' }}>Description</Typography>
                                        </Box>
                                        <Typography variant="body2" sx={{ color: '#1e293b', lineHeight: 1.7, fontWeight: 500 }}>
                                            {selectedData.description || 'No description provided.'}
                                        </Typography>
                                    </Box>

                                    {selectedData.remarks && (
                                        <Box sx={{ 
                                            p: 2.5, 
                                            borderRadius: 3, 
                                            bgcolor: alpha(theme.palette.success.main, 0.04), 
                                            border: '1px solid', 
                                            borderColor: alpha(theme.palette.success.main, 0.1),
                                            position: 'relative',
                                            '&::before': {
                                                content: '""',
                                                position: 'absolute',
                                                left: 0,
                                                top: '20%',
                                                bottom: '20%',
                                                width: '4px',
                                                bgcolor: theme.palette.success.main,
                                                borderRadius: '0 4px 4px 0'
                                            }
                                        }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                                <CommentIcon sx={{ fontSize: 18, color: theme.palette.success.main }} />
                                                <Typography variant="caption" sx={{ color: theme.palette.success.main, fontWeight: 800, textTransform: 'uppercase' }}>Admin Resolution</Typography>
                                            </Box>
                                            <Typography variant="body2" sx={{ color: theme.palette.success.dark, fontWeight: 600, fontStyle: 'italic', pl: 1 }}>
                                                "{selectedData.remarks}"
                                            </Typography>
                                        </Box>
                                    )}

                                    {selectedData.proofPicPath && (
                                        <Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                                <PhotoIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                                                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#334155' }}>Attached Proof</Typography>
                                            </Box>
                                            <Box
                                                component="img"
                                                src={getImageUrl(selectedData.proofPicPath)}
                                                sx={{ 
                                                    width: '100%', 
                                                    maxHeight: 220, 
                                                    borderRadius: 3, 
                                                    objectFit: 'cover', 
                                                    cursor: 'pointer',
                                                    transition: 'transform 0.3s ease',
                                                    border: '1px solid #e2e8f0',
                                                    '&:hover': { transform: 'scale(1.02)' }
                                                }}
                                                onClick={() => window.open(getImageUrl(selectedData.proofPicPath), '_blank')}
                                            />
                                            <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 1, color: '#94a3b8', fontWeight: 600 }}>
                                                Click image to view in full size
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        )
                    )}
                </DialogContent>

                <Box sx={{ p: 3, borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', bgcolor: '#ffffff' }}>
                    <Button 
                        onClick={() => setViewModalOpen(false)} 
                        variant="contained" 
                        sx={{ 
                            borderRadius: 2.5, 
                            px: 4, 
                            textTransform: 'none', 
                            fontWeight: 700,
                            boxShadow: 'none',
                            '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }
                        }}
                    >
                        Dismiss
                    </Button>
                </Box>
            </Dialog>

            <SuccessNotification
                open={notification.open}
                message={notification.message}
                severity={notification.severity}
                onClose={() => setNotification({ ...notification, open: false })}
            />
        </Box >
    );
}
